import React from 'react';
import * as toast from '../../toast';
import * as dateFormatter from '../../dateFormatter';
import { IModalButton } from "../../modal";
import * as modal from '../../modal';

interface IAlertSubscriptionListProps { endpoint: string }
interface IAlertSubscriptionGroup { displayTitle: string, subscriptions: Array<IAlertSubscriptionListItemProps> }
interface IAlertSubscriptionListItemProps { contentId: string, contentName: string, contentUrl: string, creationDate: Date }
interface IAlertUnsubscribeGroup { displayTitle: string, items: Array<IAlertSubscriptionListItemProps> }

interface State {
    alertSubscriptionList: Array<IAlertSubscriptionGroup>,
    showAll: boolean,
    unsubscribeLists: Array<IAlertUnsubscribeGroup>,
    isLoading: boolean,
    error: string | null
}

export class AlertSubscriptionList extends React.Component<IAlertSubscriptionListProps, State> {

    private abortController: AbortController | null = null;

    constructor(props: IAlertSubscriptionListProps) {
        super(props);
        this.state = {
            alertSubscriptionList: [],
            showAll: false,
            unsubscribeLists: [],
            isLoading: true,
            error: null
        };
        this.Unsubscribe = this.Unsubscribe.bind(this);
    }

    public componentDidMount() {
        this.GetMySubscriptions();
    }

    public componentWillUnmount() {
        // Cancel any pending requests
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    private GetMySubscriptions() {
        this.setState({ isLoading: true, error: null });

        return $.getJSON(this.props.endpoint + "/GetGroupedSubscriptions")
            .done((data) => {
                this.setState({
                    alertSubscriptionList: data || [],
                    isLoading: false
                });
            })
            .fail((xhr, status, error) => {
                console.error('Failed to load subscriptions:', error);
                this.setState({
                    isLoading: false,
                    error: 'Failed to load your subscriptions. Please try again.'
                });
            });
    }

    private Unsubscribe(params: any): void {
        const unsubscribeGroup = this.state.unsubscribeLists.find(x => x.displayTitle === params.tabKey);
        const unsubscribeList = unsubscribeGroup ? unsubscribeGroup.items : [];

        if (!unsubscribeList.length) {
            return;
        }

        this.setState({ isLoading: true });

        $.ajax({
            type: 'post',
            data: JSON.stringify(unsubscribeList.map(x => x.contentId)),
            url: this.props.endpoint + '/Unsubscribe',
            contentType: 'application/json',
            dataType: 'json'
        })
            .done(() => {
                toast.Toast.Display('Your subscriptions have been updated');
                // Immutable state update
                this.setState(prevState => ({
                    unsubscribeLists: prevState.unsubscribeLists.map(list =>
                        list.displayTitle === params.tabKey
                            ? { ...list, items: [] }
                            : list
                    ),
                    isLoading: false
                }));
                this.GetMySubscriptions();
            })
            .fail((xhr, status, error) => {
                console.error('Failed to unsubscribe:', error);
                toast.Toast.Display('Failed to update subscriptions. Please try again.');
                this.setState({ isLoading: false });
            });
    }

    public buildList(items: Array<IAlertSubscriptionListItemProps>): string {
        if (!items || items.length === 0) {
            return '';
        }

        const listItems = items.map(item =>
            `<li key="item-${item.contentId}">${item.contentName || 'Unnamed item'}</li>`
        ).join('');

        return `<ul style="margin-top:10px; list-style: none;">${listItems}</ul>`;
    }

    public SaveChanges(tabKey: string) {
        const unsubscribeGroup = this.state.unsubscribeLists.find(x => x.displayTitle === tabKey);
        const items = unsubscribeGroup?.items || [];

        if (items.length === 0) {
            return;
        }

        const unfollowBtn: IModalButton = { name: "Unfollow", func: this.Unsubscribe, params: { tabKey } };
        const cancelBtn: IModalButton = { name: "Cancel", func: null, params: null, btnClass: 'btn-default' };

        modal.Modal.Display("Are you sure you want to unfollow the following items:<br />" + this.buildList(items), [unfollowBtn, cancelBtn]);
    }

    public showAllClick = () => {
        this.setState({ showAll: true });
    }

    public showLessClick = () => {
        this.setState({ showAll: false });
    }

    public toggleUnsubscribe(displayTitle: string, id: string) {
        this.setState(prevState => {
            const unsubscribeGroup = prevState.unsubscribeLists.find(x => x.displayTitle === displayTitle);
            const alertGroup = prevState.alertSubscriptionList.find(x => x.displayTitle === displayTitle);
            const subscription = alertGroup?.subscriptions.find(x => x.contentId === id);

            if (!subscription) {
                console.warn('Subscription not found:', id);
                return null; // No state change
            }

            if (unsubscribeGroup) {
                const itemExists = unsubscribeGroup.items.some(x => x.contentId === id);

                return {
                    unsubscribeLists: prevState.unsubscribeLists.map(list =>
                        list.displayTitle === displayTitle
                            ? {
                                ...list,
                                items: itemExists
                                    ? list.items.filter(x => x.contentId !== id)
                                    : [...list.items, subscription]
                            }
                            : list
                    )
                };
            } else {
                return {
                    unsubscribeLists: [
                        ...prevState.unsubscribeLists,
                        { displayTitle, items: [subscription] }
                    ]
                };
            }
        });
    }

    // Helper method to check if item is in unsubscribe list
    private isItemChecked(displayTitle: string, contentId: string): boolean {
        const unsubscribeGroup = this.state.unsubscribeLists.find(x => x.displayTitle === displayTitle);
        if (!unsubscribeGroup) return true;
        return !unsubscribeGroup.items.some(x => x.contentId === contentId);
    }

    // Helper method to check if save button should be disabled
    private isSaveDisabled(displayTitle: string): boolean {
        const unsubscribeGroup = this.state.unsubscribeLists.find(x => x.displayTitle === displayTitle);
        return !unsubscribeGroup || unsubscribeGroup.items.length === 0;
    }

    private renderSubscriptionItem(subscription: IAlertSubscriptionListItemProps, groupTitle: string) {
        const isChecked = this.isItemChecked(groupTitle, subscription.contentId);

        return (
            <li key={subscription.contentId} className="margin-top-5 margin-btm-10" style={{ borderRadius: '4px', border: '1px solid #c2c2c3' }}>
                <div style={{ padding: '10px 15px' }} className="info row justify-content-between">
                    <div className="col-auto d-flex align-items-center form-check padding-left-10">
                        <input
                            type="checkbox"
                            className="form-check-input miso-checkbox margin-right-10 margin-top-0"
                            id={subscription.contentId}
                            checked={isChecked}
                            onChange={() => this.toggleUnsubscribe(groupTitle, subscription.contentId)}
                        />
                        <label className="form-check-label" htmlFor={subscription.contentId}>
                            {subscription.contentName || 'Unnamed'}
                        </label>
                    </div>
                    <div className="col-auto dark-grey">
                        Followed {subscription.creationDate ? dateFormatter.DateFormatter.Format(subscription.creationDate) : 'Unknown date'}
                    </div>
                </div>
            </li>
        );
    }

    public render() {
        const { isLoading, error, alertSubscriptionList, showAll } = this.state;

        if (isLoading && alertSubscriptionList.length === 0) {
            return (
                <div className="text-center padding-20">
                    <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
                    <p>Loading subscriptions...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="alert alert-danger" role="alert">
                    {error}
                    <button className="btn btn-link" onClick={() => this.GetMySubscriptions()}>
                        Try Again
                    </button>
                </div>
            );
        }

        if (alertSubscriptionList.length === 0) {
            return <p>You currently have no subscriptions</p>;
        }

        return (
            <div>
                <div className="row padding-btm-20">
                    <h3 className="col-12 blue">My Alerts</h3>
                </div>

                <div className="row padding-btm-20">
                    <div className="tab-content-block col-12">
                        <ul className="nav nav-tabs">
                            {alertSubscriptionList.map((group, gi) => {
                                const className = gi === 0 ? "nav-link active" : "nav-link";
                                const target = "#myAlerts" + group.displayTitle + "Tab";
                                return (
                                    <li key={group.displayTitle} className="nav-item">
                                        <a className={className} data-bs-toggle="tab" data-bs-target={target} href={target}>
                                            {group.displayTitle}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                <div className="tab-content">
                    {alertSubscriptionList.map((group, gi) => {
                        const className = gi === 0 ? "tab-pane fade show active in" : "tab-pane fade";
                        const id = "myAlerts" + group.displayTitle + "Tab";

                        // Sort subscriptions alphabetically by contentName (memoized per group)
                        const sortedSubscriptions = [...group.subscriptions].sort((a, b) =>
                            (a.contentName || '').localeCompare(b.contentName || '', undefined, { sensitivity: 'base' })
                        );

                        const displayedSubscriptions = showAll
                            ? sortedSubscriptions
                            : sortedSubscriptions.slice(0, 10);

                        return (
                            <div key={id} id={id} className={className}>
                                <div className="row">
                                    <div className="col-12">
                                        {sortedSubscriptions.length > 0 ? (
                                            <div>
                                                <p>
                                                    By unchecking an item below and clicking the "Save Changes" button,
                                                    you will no longer get notified of changes made to that particular{' '}
                                                    {group.displayTitle.toLowerCase().substring(0, group.displayTitle.length - 1)}.
                                                </p>
                                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                                    {displayedSubscriptions.map(subscription =>
                                                        this.renderSubscriptionItem(subscription, group.displayTitle)
                                                    )}
                                                </ul>
                                                {sortedSubscriptions.length > 10 && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <button
                                                            className="btn btn-link btn-small blue"
                                                            onClick={showAll ? this.showLessClick : this.showAllClick}
                                                        >
                                                            {showAll ? "Show Less" : "Show More"}
                                                        </button>
                                                    </div>
                                                )}
                                                <hr />
                                                <div className="row">
                                                    <div className="col-12">
                                                        <button
                                                            className="btn btn-primary"
                                                            style={{ float: 'right' }}
                                                            onClick={() => this.SaveChanges(group.displayTitle)}
                                                            disabled={isLoading || this.isSaveDisabled(group.displayTitle)}
                                                        >
                                                            {isLoading ? 'Saving...' : 'Save Changes'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p>You currently have no {group.displayTitle} subscriptions</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}